// DownloadFinancePackageButton — the "Download Finance Package (.xlsx)" action.
//
// Gathers the finance data the dashboard already loads (via the finance hooks),
// assembles the FinancePackageData snapshot, and triggers the branded multi-sheet
// workbook download (lib/financePackage.ts). exceljs is dynamically imported by
// the builder, so this button adds no weight to the main bundle until clicked.
//
// Self-contained: it owns its own hooks so any page can drop it in next to Print.
// Endpoints that haven't synced degrade gracefully — the builder writes a
// "(syncing)" note in that sheet rather than failing the whole export.

import { useState } from "react";
import { FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import {
  useFinanceSummary,
  useFinanceAvailable,
  useFinanceByObject,
  useFinanceOpenPos,
  useFy27Build,
  useFy27Bridge,
  useFy27V2,
  useProjectsManage,
  useNonprofitSummary,
  useNonprofitIncomeBySource,
  useNonprofitExpensesByCategory,
  useNonprofitTimeseries,
} from "../../hooks/useFinance";
import { nextFiscalYear, fyContextFromAsOf } from "../../lib/fiscal";
import {
  downloadFinancePackage,
  type FinancePackageData,
} from "../../lib/financePackage";
import { downloadFinancePackagePdf } from "../../lib/financePackagePdf";

export default function DownloadFinancePackageButton({
  className = "",
  format = "xlsx",
}: {
  className?: string;
  // "xlsx" = the multi-sheet workbook; "pdf" = the branded board packet PDF that
  // renders the SAME content/sections (not a page screenshot).
  format?: "xlsx" | "pdf";
}) {
  const { data: summary } = useFinanceSummary();
  const { data: available } = useFinanceAvailable();
  const { data: byObject } = useFinanceByObject("expenditure");
  const { data: openPos } = useFinanceOpenPos();
  const { data: fy27Build } = useFy27Build();
  const { data: fy27Bridge } = useFy27Bridge();
  const { data: fy27V2 } = useFy27V2();
  const { data: projects } = useProjectsManage();
  const { data: nonprofit } = useNonprofitSummary();
  const { data: npIncome } = useNonprofitIncomeBySource();
  const { data: npExpenses } = useNonprofitExpensesByCategory();
  const { data: npTimeseries } = useNonprofitTimeseries();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const asOf = summary?.as_of_date ?? null;
  // curFy = the FY the position/actuals represent (from as-of); fyc.isPriorYear
  // flags when that's behind the year we're in now, so the exported package
  // labels the position sections "(prior year)" instead of implying current.
  const fyc = fyContextFromAsOf(asOf);
  const curFy = fyc.dataFy ?? fyc.currentFy;
  // Budget label = the budget's OWN fiscal year (pinned in dash_fy27_v2_meta),
  // NOT clock-derived next-year. Otherwise the FY27 budget mislabels as "FY28"
  // the moment the position year rolls to FY27 (7/1). Falls back to the old
  // clock offset if meta is unavailable.
  const nextFy = fy27V2?.meta?.budget_fy ?? nextFiscalYear(curFy);

  async function handleClick() {
    setBusy(true);
    setErr(null);
    try {
      const data: FinancePackageData = {
        asOf,
        curFy,
        curFyIsPrior: fyc.isPriorYear,
        nextFy,
        summary: summary ?? null,
        available: available ?? null,
        byObject: byObject?.data ?? null,
        openPos: openPos?.data ?? null,
        fy27Build: fy27Build ?? null,
        fy27Bridge: fy27Bridge ?? null,
        fy27V2: fy27V2 ?? null,
        projects: projects?.projects ?? null,
        nonprofit: nonprofit ?? null,
        npIncome: npIncome ?? null,
        npExpenses: npExpenses ?? null,
        npTimeseries: npTimeseries ?? null,
        preparedBy: "Ember (via Claude)",
      };
      if (format === "pdf") await downloadFinancePackagePdf(data);
      else await downloadFinancePackage(data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Export failed");
    } finally {
      setBusy(false);
    }
  }

  const isPdf = format === "pdf";
  const label = isPdf
    ? "Download Board Packet (.pdf)"
    : "Download Finance Package (.xlsx)";
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      title={
        err
          ? `Export failed: ${err}`
          : isPdf
            ? "Build the branded board packet PDF — the same sections as the workbook, formatted for the meeting"
            : "Build the branded multi-sheet Finance Package (.xlsx) — one tab per perspective"
      }
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-dcvfd-accent text-white hover:opacity-90 transition disabled:opacity-50 disabled:cursor-wait print:hidden ${className}`}
    >
      {busy ? (
        <Loader2 size={14} className="animate-spin" aria-hidden="true" />
      ) : isPdf ? (
        <FileText size={14} aria-hidden="true" />
      ) : (
        <FileSpreadsheet size={14} aria-hidden="true" />
      )}
      {busy ? "Building…" : label}
    </button>
  );
}
